// Service de gestion des notifications push PWA
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convertir la clé VAPID en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Vérifier si l'app est en mode standalone (installée sur l'écran d'accueil)
export const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// Vérifier si on est sur iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Vérifier si les notifications sont supportées
export const isPushSupported = () => {
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const hasNotification = 'Notification' in window;
  
  console.log('[Push] Support check:', { hasServiceWorker, hasPushManager, hasNotification, isStandalone: isStandalone(), isIOS: isIOS() });
  
  // Sur iOS, les notifications ne sont supportées qu'en mode standalone (PWA installée)
  if (isIOS() && !isStandalone()) {
    console.log('[Push] iOS détecté mais pas en mode standalone - notifications non disponibles');
    return false;
  }
  
  return hasServiceWorker && hasPushManager && hasNotification;
};

// Vérifier l'état actuel des permissions
export const getNotificationPermission = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

// Enregistrer le Service Worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('[Push] Service Worker non supporté');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push] Service Worker enregistré:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[Push] Erreur enregistrement SW:', error);
    return null;
  }
};

// Demander la permission pour les notifications
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Notifications non supportées sur ce navigateur' };
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission:', permission);
    
    if (permission === 'granted') {
      return { success: true, permission };
    } else {
      return { success: false, error: 'Permission refusée', permission };
    }
  } catch (error) {
    console.error('[Push] Erreur permission:', error);
    return { success: false, error: error.message };
  }
};

// S'abonner aux notifications push
export const subscribeToPush = async (token) => {
  console.log('[Push] Début subscribeToPush');
  
  if (!isPushSupported()) {
    console.log('[Push] Push non supporté');
    return { success: false, error: 'Push non supporté' };
  }

  try {
    // Récupérer la clé publique VAPID du serveur
    console.log('[Push] Récupération clé VAPID...');
    const vapidResponse = await fetch(`${API_URL}/api/push/vapid-key`);
    const { public_key } = await vapidResponse.json();
    console.log('[Push] Clé VAPID reçue:', public_key ? 'OK' : 'MANQUANTE');
    
    if (!public_key) {
      return { success: false, error: 'Clé VAPID non disponible' };
    }

    // Enregistrer le SW si pas déjà fait
    console.log('[Push] Attente du Service Worker...');
    const registration = await navigator.serviceWorker.ready;
    console.log('[Push] Service Worker prêt:', registration.scope);
    
    // Vérifier si déjà abonné
    let subscription = await registration.pushManager.getSubscription();
    console.log('[Push] Subscription existante:', subscription ? 'OUI' : 'NON');
    
    if (!subscription) {
      // Créer un nouvel abonnement
      console.log('[Push] Création nouvel abonnement...');
      console.log('[Push] VAPID key length:', public_key?.length);
      try {
        const convertedKey = urlBase64ToUint8Array(public_key);
        console.log('[Push] Converted key length:', convertedKey?.length);
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedKey
        });
        console.log('[Push] Nouvel abonnement créé!');
        console.log('[Push] Endpoint:', subscription.endpoint?.substring(0, 50) + '...');
      } catch (subError) {
        console.error('[Push] Erreur création subscription:', subError);
        console.error('[Push] Error name:', subError.name);
        console.error('[Push] Error message:', subError.message);
        return { success: false, error: 'Erreur lors de la création de l\'abonnement: ' + subError.message };
      }
    } else {
      console.log('[Push] Utilisation abonnement existant');
      console.log('[Push] Existing endpoint:', subscription.endpoint?.substring(0, 50) + '...');
    }

    // Envoyer l'abonnement au serveur
    console.log('[Push] Envoi au serveur...');
    console.log('[Push] Subscription object:', subscription);
    
    // Get subscription data - handle both toJSON() and direct object
    let subscriptionData;
    try {
      subscriptionData = subscription.toJSON ? subscription.toJSON() : subscription;
      console.log('[Push] Subscription data to send:', JSON.stringify(subscriptionData).substring(0, 100) + '...');
    } catch (e) {
      console.error('[Push] Error converting subscription:', e);
      return { success: false, error: 'Erreur conversion subscription' };
    }
    
    if (!subscriptionData || !subscriptionData.endpoint) {
      console.error('[Push] Invalid subscription data - no endpoint');
      return { success: false, error: 'Subscription invalide (pas d\'endpoint)' };
    }
    
    const response = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subscription: subscriptionData
      })
    });

    if (response.ok) {
      console.log('[Push] ✅ Abonnement enregistré sur le serveur');
      return { success: true, subscription };
    } else {
      const error = await response.json();
      console.error('[Push] ❌ Erreur serveur:', error);
      return { success: false, error: error.detail || 'Erreur serveur' };
    }
  } catch (error) {
    console.error('[Push] ❌ Erreur abonnement:', error);
    return { success: false, error: error.message };
  }
};

// Se désabonner des notifications
export const unsubscribeFromPush = async (token) => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Désabonner côté navigateur
      await subscription.unsubscribe();
      
      // Informer le serveur
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });
      
      console.log('[Push] Désabonnement réussi');
      return { success: true };
    }
    
    return { success: true, message: 'Pas d\'abonnement actif' };
  } catch (error) {
    console.error('[Push] Erreur désabonnement:', error);
    return { success: false, error: error.message };
  }
};

// Vérifier si l'utilisateur est abonné
export const isSubscribed = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

// Envoyer une notification de test (local)
export const sendTestNotification = () => {
  if (Notification.permission === 'granted') {
    new Notification('PlayMCB', {
      body: 'Les notifications fonctionnent ! 🎉',
      icon: '/logo-mcb.png'
    });
  }
};
